import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState, fmtDate } from "@/components/ui-kit";
import { Plus, MessageCircle, Send, CheckCircle2, Trash2, FileText, History, Sparkles } from "lucide-react";

export default function WhatsApp() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("compose"); // compose / templates / history
  const [templates, setTemplates] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [openTpl, setOpenTpl] = useState(null);
  const [tpl, setTpl] = useState({ name: "", category: "general", message_en: "", message_ar: "", variables: [] });

  // Compose
  const [composeOpen, setComposeOpen] = useState(false);
  const [filter, setFilter] = useState({ template_id: "", recipient_type: "pharmacies", governorate: "", city: "", kol_classification: "", classification: "", language: "en", scheduled_at: "" });
  const [recipients, setRecipients] = useState([]);
  const [broadcastId, setBroadcastId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [tpls, bs] = await Promise.all([api.get("/whatsapp/templates"), api.get("/whatsapp/broadcasts")]);
    setTemplates(tpls.data); setBroadcasts(bs.data);
  };
  useEffect(() => { load(); }, []);

  const saveTpl = async (e) => {
    e.preventDefault();
    const payload = { ...tpl, variables: typeof tpl.variables === "string" ? tpl.variables.split(",").map(s => s.trim()).filter(Boolean) : tpl.variables };
    if (openTpl?.id) await api.put(`/whatsapp/templates/${openTpl.id}`, payload);
    else await api.post("/whatsapp/templates", payload);
    setOpenTpl(null); setTpl({ name: "", category: "general", message_en: "", message_ar: "", variables: [] });
    await load();
  };

  const delTpl = async (id) => {
    if (!window.confirm("Delete template?")) return;
    await api.delete(`/whatsapp/templates/${id}`);
    await load();
  };

  const generateBroadcast = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...filter };
      // Strip empty
      Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/whatsapp/broadcasts", payload);
      setBroadcastId(data.broadcast.id);
      setRecipients(data.recipients);
      setComposeOpen(false);
      await load();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const markSent = async (idx) => {
    const r = recipients[idx];
    await api.post(`/whatsapp/broadcasts/${broadcastId}/mark-sent`, {
      recipient_id: r.id, recipient_name: r.name, recipient_phone: r.phone,
    });
    setRecipients(recipients.map((x, i) => i === idx ? { ...x, sent: true } : x));
  };

  const sendAndMark = async (idx) => {
    const r = recipients[idx];
    window.open(r.wa_url, "_blank");
    setTimeout(() => markSent(idx), 800);
  };

  const sentRate = (b) => b.recipient_count > 0 ? Math.round((b.sent_count / b.recipient_count) * 100) : 0;

  return (
    <div data-testid="whatsapp-page">
      <PageHeader
        title={t("whatsapp")}
        subtitle="Send personalized WhatsApp messages to KOLs, pharmacies & doctors — opens directly in WhatsApp"
      />

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <div className="flex gap-1">
          {[
            { key: "compose", label: "Compose & Send", icon: Send },
            { key: "templates", label: "Templates", icon: FileText },
            { key: "history", label: "History", icon: History },
          ].map(tabItem => (
            <button
              key={tabItem.key}
              onClick={() => setTab(tabItem.key)}
              data-testid={`tab-${tabItem.key}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors inline-flex items-center gap-2 ${
                tab === tabItem.key
                  ? "border-blue-700 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <tabItem.icon className="w-4 h-4"/>
              {tabItem.label}
            </button>
          ))}
        </div>
      </div>

      {/* COMPOSE TAB */}
      {tab === "compose" && (
        <div className="space-y-6">
          {recipients.length === 0 ? (
            <Card className="p-10 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 grid place-items-center mb-4">
                <MessageCircle className="w-8 h-8 text-emerald-600"/>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope" }}>Start a New Broadcast</h3>
              <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                Pick a template, filter recipients (KOL-A doctors, A+ pharmacies, by territory), and send personalized messages with one tap.
              </p>
              <Btn onClick={() => setComposeOpen(true)} data-testid="new-broadcast-btn">
                <Sparkles className="w-4 h-4"/> New Broadcast
              </Btn>
            </Card>
          ) : (
            <Card>
              <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-emerald-50">
                <div>
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
                    Ready to Send · {recipients.length} recipient{recipients.length !== 1 && "s"}
                  </h3>
                  <p className="text-xs text-slate-600 mt-1">
                    Sent: {recipients.filter(r => r.sent).length}/{recipients.length} · Click "Send" to open WhatsApp & mark as sent
                  </p>
                </div>
                <div className="flex gap-2">
                  <Btn variant="ghost" onClick={() => { setRecipients([]); setBroadcastId(null); }}>Clear</Btn>
                  <Btn onClick={() => setComposeOpen(true)}><Plus className="w-4 h-4"/>New</Btn>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b text-xs uppercase tracking-wider text-slate-500">
                      <th className="py-2 px-3 text-start">#</th>
                      <th className="py-2 px-3 text-start">Name</th>
                      <th className="py-2 px-3 text-start">Phone</th>
                      <th className="py-2 px-3 text-start">City</th>
                      <th className="py-2 px-3 text-start">Class</th>
                      <th className="py-2 px-3 text-start">Preview</th>
                      <th className="py-2 px-3 text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r, i) => (
                      <tr key={r.id} className={`border-b border-slate-100 ${r.sent ? "bg-emerald-50/40" : "hover:bg-slate-50"}`}>
                        <td className="py-2 px-3 text-xs text-slate-500">{i + 1}</td>
                        <td className="py-2 px-3 font-semibold">{r.name}</td>
                        <td className="py-2 px-3 font-mono text-xs">+{r.phone}</td>
                        <td className="py-2 px-3 text-slate-600">{r.city || "-"}</td>
                        <td className="py-2 px-3"><Badge color="slate">{r.classification || "-"}</Badge></td>
                        <td className="py-2 px-3 text-xs text-slate-600 max-w-md truncate" title={r.rendered_message}>{r.rendered_message}</td>
                        <td className="py-2 px-3 text-end">
                          {r.sent ? (
                            <Badge color="green"><CheckCircle2 className="w-3 h-3 inline me-1"/>Sent</Badge>
                          ) : (
                            <button
                              onClick={() => sendAndMark(i)}
                              data-testid={`send-${r.id}`}
                              className="inline-flex items-center gap-1 bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded hover:bg-emerald-500"
                            >
                              <Send className="w-3 h-3"/> Send
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* TEMPLATES TAB */}
      {tab === "templates" && (
        <div className="space-y-4">
          {(user.role === "admin" || user.role === "supervisor") && (
            <div className="flex justify-end">
              <Btn onClick={() => { setOpenTpl({ id: null }); setTpl({ name: "", category: "general", message_en: "", message_ar: "", variables: [] }); }} data-testid="new-template-btn">
                <Plus className="w-4 h-4"/> New Template
              </Btn>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map(tp => (
              <Card key={tp.id} className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge color={tp.category === "promo" ? "amber" : tp.category === "launch" ? "blue" : tp.category === "reminder" ? "purple" : "slate"}>{tp.category}</Badge>
                    <h3 className="text-lg font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }}>{tp.name}</h3>
                  </div>
                  {(user.role === "admin" || user.role === "supervisor") && (
                    <div className="flex gap-1">
                      <button onClick={() => { setOpenTpl(tp); setTpl({ ...tp, variables: (tp.variables || []).join(", ") }); }} className="text-xs font-semibold text-blue-700 px-2 py-1 hover:bg-blue-50 rounded">Edit</button>
                      <button onClick={() => delTpl(tp.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-600"/></button>
                    </div>
                  )}
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="overline mb-1">English</div>
                    <div className="bg-slate-50 border border-slate-200 rounded p-3 text-slate-700 whitespace-pre-wrap">{tp.message_en}</div>
                  </div>
                  {tp.message_ar && (
                    <div>
                      <div className="overline mb-1">Arabic</div>
                      <div className="bg-slate-50 border border-slate-200 rounded p-3 text-slate-700 whitespace-pre-wrap" dir="rtl">{tp.message_ar}</div>
                    </div>
                  )}
                  {tp.variables?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {tp.variables.map(v => <Badge key={v} color="blue">{`{${v}}`}</Badge>)}
                    </div>
                  )}
                </div>
              </Card>
            ))}
            {templates.length === 0 && <div className="col-span-full"><EmptyState message="No templates yet"/></div>}
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === "history" && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-y text-xs uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-3 text-start">Date</th>
                  <th className="py-2 px-3 text-start">Template</th>
                  <th className="py-2 px-3 text-start">Recipients</th>
                  <th className="py-2 px-3 text-start">Type</th>
                  <th className="py-2 px-3 text-start">Lang</th>
                  <th className="py-2 px-3 text-start">By</th>
                  <th className="py-2 px-3 text-start">Sent Rate</th>
                </tr>
              </thead>
              <tbody>
                {broadcasts.map(b => (
                  <tr key={b.id} className="border-b border-slate-100">
                    <td className="py-2 px-3 text-xs">{fmtDate(b.created_at)}</td>
                    <td className="py-2 px-3 font-semibold">{b.template_name}</td>
                    <td className="py-2 px-3">{b.recipient_count}</td>
                    <td className="py-2 px-3"><Badge color={b.recipient_type === "doctors" ? "purple" : "blue"}>{b.recipient_type}</Badge></td>
                    <td className="py-2 px-3"><Badge color="slate">{b.language?.toUpperCase()}</Badge></td>
                    <td className="py-2 px-3 text-slate-700">{b.created_by_name}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${sentRate(b)}%` }}/>
                        </div>
                        <span className="text-xs font-semibold">{b.sent_count}/{b.recipient_count}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {broadcasts.length === 0 && <EmptyState message="No broadcasts sent yet"/>}
          </div>
        </Card>
      )}

      {/* New broadcast modal */}
      <Modal open={composeOpen} onClose={() => setComposeOpen(false)} title="New Broadcast">
        <form onSubmit={generateBroadcast} className="space-y-3" data-testid="broadcast-form">
          <Select label="Template *" required value={filter.template_id} onChange={(e) => setFilter({...filter, template_id: e.target.value})} data-testid="broadcast-template">
            <option value="">--Select--</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name} · {t.category}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Send to *" value={filter.recipient_type} onChange={(e) => setFilter({...filter, recipient_type: e.target.value})}>
              <option value="pharmacies">Pharmacies</option>
              <option value="doctors">Doctors</option>
            </Select>
            <Select label="Language" value={filter.language} onChange={(e) => setFilter({...filter, language: e.target.value})}>
              <option value="en">English</option>
              <option value="ar">Arabic</option>
            </Select>
            <Select label="Governorate (optional)" value={filter.governorate} onChange={(e) => setFilter({...filter, governorate: e.target.value})}>
              <option value="">All</option>
              <option>Gharbia</option>
              <option>Kafr El Sheikh</option>
            </Select>
            <Input label="City (optional)" value={filter.city} onChange={(e) => setFilter({...filter, city: e.target.value})}/>
            {filter.recipient_type === "doctors" && (
              <Select label="KOL Class (optional)" value={filter.kol_classification} onChange={(e) => setFilter({...filter, kol_classification: e.target.value})}>
                <option value="">All</option>
                <option>KOL-A</option><option>KOL-B</option><option>KOL-C</option>
              </Select>
            )}
            {filter.recipient_type === "pharmacies" && (
              <Select label="Class (optional)" value={filter.classification} onChange={(e) => setFilter({...filter, classification: e.target.value})}>
                <option value="">All</option>
                <option>A+</option><option>A</option><option>B</option><option>C</option>
              </Select>
            )}
          </div>
          <div className="text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded p-3">
            💡 Recipients without WhatsApp/Mobile numbers will be skipped. Phones are auto-formatted for Egypt (+20).
          </div>
          <Input label="Schedule for later (optional)" type="datetime-local" value={filter.scheduled_at} onChange={(e) => setFilter({...filter, scheduled_at: e.target.value})} data-testid="schedule-at"/>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Btn variant="ghost" type="button" onClick={() => setComposeOpen(false)}>{t("cancel")}</Btn>
            <Btn type="submit" disabled={busy} data-testid="generate-broadcast"><Sparkles className="w-4 h-4"/>{busy ? t("loading") : "Generate"}</Btn>
          </div>
        </form>
      </Modal>

      {/* Template editor */}
      <Modal open={!!openTpl} onClose={() => setOpenTpl(null)} title={openTpl?.id ? "Edit Template" : "New Template"} maxWidth="max-w-2xl">
        <form onSubmit={saveTpl} className="space-y-3" data-testid="template-form">
          <Input label="Name *" required value={tpl.name} onChange={(e) => setTpl({...tpl, name: e.target.value})}/>
          <Select label="Category" value={tpl.category} onChange={(e) => setTpl({...tpl, category: e.target.value})}>
            <option value="general">General</option>
            <option value="promo">Promo</option>
            <option value="launch">Launch</option>
            <option value="reminder">Reminder</option>
            <option value="follow_up">Follow-up</option>
          </Select>
          <Textarea label="English Message *" rows={4} required value={tpl.message_en} onChange={(e) => setTpl({...tpl, message_en: e.target.value})}/>
          <Textarea label="Arabic Message" rows={4} value={tpl.message_ar} onChange={(e) => setTpl({...tpl, message_ar: e.target.value})} dir="rtl"/>
          <Input label="Variables (comma-separated, e.g. name, pharmacy, rep)" value={typeof tpl.variables === "string" ? tpl.variables : (tpl.variables || []).join(", ")} onChange={(e) => setTpl({...tpl, variables: e.target.value})}/>
          <div className="text-xs text-slate-500 bg-slate-50 border rounded p-3">
            Use placeholders in messages: <code className="font-mono">{"{name}"}</code>, <code className="font-mono">{"{pharmacy}"}</code>, <code className="font-mono">{"{doctor}"}</code>, <code className="font-mono">{"{city}"}</code>, <code className="font-mono">{"{rep}"}</code>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Btn variant="ghost" type="button" onClick={() => setOpenTpl(null)}>{t("cancel")}</Btn>
            <Btn type="submit">{t("save")}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
